import React, { useState, useCallback, useMemo, memo } from "react";
import {
  LayoutDashboard, ArrowDownCircle, ArrowUpCircle,
  ClipboardList, BarChart3, LogOut,
  Menu, FileText, Shield, Database, Activity, Users, RefreshCw, Briefcase, Package2,
  ChevronDown, ChevronUp, UserCog, ShoppingCart, Wallet
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import { useToast } from "./Toast";

const ICON_MAP = {
  LayoutDashboard, ArrowDownCircle, ArrowUpCircle, ClipboardList, BarChart3,
  FileText, Shield, Database, Activity, Users, RefreshCw, Briefcase, Package2, UserCog, ShoppingCart, Wallet
};

const HR_ITEMS = [
  { id:"personel",    label:"Personel",          iconName:"Users", iconSize:16 },
  { id:"bagtapyan",    label:"Bagtapyan",         iconName:"FileText", iconSize:16 },
  { id:"binkar",       label:"Binkar",            iconName:"FileText", iconSize:16 },
  { id:"minseldik",   label:"Minseldik",         iconName:"FileText", iconSize:16 },
  { id:"tahor",        label:"Tahor",             iconName:"FileText", iconSize:16 },
  { id:"dosir",        label:"Dosir",             iconName:"Database", iconSize:16 },
  { id:"dosel",        label:"Dosel",             iconName:"Database", iconSize:16 },
  { id:"pensiun",      label:"Pensiun",           iconName:"Users", iconSize:16 },
  { id:"kekuatan",     label:"Kekuatan Personil", iconName:"Activity", iconSize:16 },
];

export const MENU = [
  { id:"dashboard",   label:"Dashboard",            iconName:"LayoutDashboard", iconSize:18, roles:["ADMIN","BENDAHARA","PEJABAT","EKSEKUTIF","VERIFIKATOR","FINANCE","PERSONEL"] },
  { id:"budget",      label:"RENDAL (Perencanaan)",  iconName:"BarChart3", iconSize:18, roles:["ADMIN","PEJABAT","EKSEKUTIF"] },
  { id:"revisi",      label:"Revisi Anggaran",       iconName:"RefreshCw", iconSize:18, roles:["ADMIN","PEJABAT","VERIFIKATOR","EKSEKUTIF"] },
  { id:"penerimaan",  label:"Penerimaan",            iconName:"ArrowDownCircle", iconSize:18, roles:["ADMIN","BENDAHARA"] },
  { id:"payment",     label:"FINANCE (Pembayaran)",  iconName:"ClipboardList", iconSize:18, roles:["ADMIN","BENDAHARA","PEJABAT","VERIFIKATOR"] },
  { id:"pengeluaran", label:"Pengeluaran",           iconName:"ArrowUpCircle", iconSize:18, roles:["ADMIN","BENDAHARA"] },
  { id:"bendahara",   label:"Keuangan & Akunting",   iconName:"Wallet", iconSize:18, roles:["ADMIN","BENDAHARA","PEJABAT","EKSEKUTIF"] },
  { id:"master",      label:"Master Data",           iconName:"Database", iconSize:18, roles:["ADMIN"] },
  { id:"assets",      label:"Manajemen Aset",        iconName:"Briefcase", iconSize:18, roles:["ADMIN","PEJABAT","EKSEKUTIF","FINANCE"] },
  { id:"inventory",   label:"Manajemen Inventaris",  iconName:"Package2", iconSize:18, roles:["ADMIN","PEJABAT","EKSEKUTIF","FINANCE"] },
  { id:"procurement", label:"BALAKADA (Pengadaan)",   iconName:"ShoppingCart", iconSize:18, roles:["ADMIN","FINANCE","PEJABAT","EKSEKUTIF"],
    children: [
      { id:"procurement-pr",      label:"PROGAR (Penganggaran)", iconName:"ClipboardList", iconSize:16 },
      { id:"procurement-kontrak", label:"PPK (Komitmen)",        iconName:"FileText",      iconSize:16 },
    ]
  },
  { id:"reports",     label:"Laporan & Analisis",    iconName:"FileBarChart", iconSize:18, roles:["ADMIN","PEJABAT","EKSEKUTIF","BENDAHARA"] },
  { id:"audit-trail", label:"Audit Trail",           iconName:"Activity", iconSize:18, roles:["ADMIN","EKSEKUTIF"] },
  { id:"hr",          label:"Manajemen HR",        iconName:"UserCog", iconSize:18, roles:["ADMIN","PEJABAT","EKSEKUTIF","PERSONEL"], children: HR_ITEMS },
  { id:"admin",       label:"IT Admin",            iconName:"Shield", iconSize:18, roles:["ADMIN"] },
];

const getIcon = (iconName, size) => {
  const IconComponent = ICON_MAP[iconName];
  return IconComponent ? <IconComponent size={size} /> : null;
};

export const roleColors = { ADMIN:"#7C3AED", BENDAHARA:"#2563EB", PEJABAT:"#D97706", EKSEKUTIF:"#059669", VERIFIKATOR:"#DC2626", FINANCE:"#0891B2", PERSONEL:"#6366F1" };

export const getMenuLabel = (id) => {
  for (const m of MENU) {
    if (m.id === id) return m.label;
    if (m.children) {
      for (const c of m.children) {
        if (c.id === id) return c.label;
      }
    }
  }
  return id;
};

const MenuButton = ({ item, active, setActive, collapsed, isParent, isChild, isExpanded, onToggle }) => {
  const isActive = item?.id === active;

  const handleClick = () => {
    if (isParent) {
      if (collapsed) { setActive(item.id); }
      else { onToggle(item.id); setActive(item.id); }
    } else {
      setActive(item.id);
    }
  };

  const baseStyle = {
    display: "flex", alignItems: "center", gap: 10,
    padding: isChild ? "8px 12px 8px 40px" : "10px 12px",
    borderRadius: 10, border: "none", cursor: "pointer",
    background: isActive ? "#1E3A5F" : "transparent",
    color: isActive ? "#60A5FA" : "#94A3B8",
    fontFamily: "inherit", fontSize: isChild ? 12 : 13,
    fontWeight: isActive ? 700 : 500, transition: "all 0.15s",
    textAlign: "left", width: "100%",
    justifyContent: collapsed ? "center" : "flex-start",
  };

  return (
    <button onClick={handleClick} style={baseStyle}
      onMouseEnter={e => !isActive && (e.currentTarget.style.background = "#1E293B")}
      onMouseLeave={e => !isActive && (e.currentTarget.style.background = "transparent")}>
      <span style={{ flexShrink: 0 }}>{getIcon(item.iconName, item.iconSize)}</span>
      {!collapsed && (
        <>
          <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
          {isParent && (isExpanded ? <ChevronUp size={14} style={{ flexShrink: 0 }} /> : <ChevronDown size={14} style={{ flexShrink: 0 }} />)}
        </>
      )}
    </button>
  );
};

const Sidebar = ({ active, setActive, collapsed, setCollapsed }) => {
  const { user, logout, hasPermission } = useAuth();
  const { toast } = useToast();
  const [expandedItems, setExpandedItems] = useState({});

  const handleLogout = () => {
    toast(`Sampai jumpa, ${user?.name}!`, "warning");
    setTimeout(() => logout(), 500);
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const accessible = MENU.filter(m => {
    if (!m.roles.includes(user?.role)) return false;
    if (m.permissions && !hasPermission(...m.permissions)) return false;
    return true;
  });

  const menuItems = [];
  accessible.forEach(item => {
    menuItems.push({ item, isChild: false });
    if (item.children && expandedItems[item.id]) {
      item.children.forEach(child => {
        menuItems.push({ item: child, isChild: true });
      });
    }
  });

  return (
    <div style={{ width: collapsed ? 64 : 240, minHeight: "100vh", background: "#0F172A",
      display: "flex", flexDirection: "column", transition: "width 0.25s ease", flexShrink: 0, zIndex: 10 }}>
      <div style={{ padding: "20px 16px", borderBottom: "1px solid #1E293B", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, overflow: "hidden", flexShrink: 0,
          background: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src="/logo-rspad.png" alt="Logo" style={{ width: 34, height: 34, objectFit: "contain" }} onError={e => e.target.style.display='none'} />
        </div>
        {!collapsed && <div>
          <div style={{ color: "white", fontWeight: 800, fontSize: 13, letterSpacing: "-0.01em", lineHeight: 1.2 }}>IFMS</div>
          <div style={{ color: "#475569", fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>RSPAD Gatot Soebroto</div>
        </div>}
        <button onClick={() => setCollapsed(!collapsed)}
          style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#64748B", padding: 4, display: "flex", flexShrink: 0 }}>
          <Menu size={16} />
        </button>
      </div>
      <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
        {menuItems.map((entry, idx) => {
          const isParent = entry.item.children && entry.item.children.length > 0;
          return (
            <MenuButton
              key={`${entry.item.id}-${idx}`}
              item={entry.item}
              active={active}
              setActive={setActive}
              collapsed={collapsed}
              isParent={isParent}
              isChild={entry.isChild}
              isExpanded={expandedItems[entry.item.id]}
              onToggle={toggleExpanded}
            />
          );
        })}
      </nav>
      <div style={{ padding: "12px 8px", borderTop: "1px solid #1E293B" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#1E293B" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: roleColors[user?.role] || "#2563EB",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: "white", fontSize: 12, fontWeight: 700 }}>{user?.name?.[0] || "U"}</span>
          </div>
          {!collapsed && <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ color: "white", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name}</div>
            <div style={{ color: roleColors[user?.role] || "#60A5FA", fontSize: 10, fontWeight: 600 }}>{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>}
          {!collapsed && <button onClick={handleLogout} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B", padding: 4, flexShrink: 0 }}><LogOut size={15} /></button>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;